import React, { useEffect, useState } from 'react';
import OdfjellInputComponent, { InputChangeData } from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import { useBunkerContext } from 'src/context/BunkerContext';
import { BunkerReceived } from 'src/types/ViewModel/Bunkering/BunkerReceived';
import { FuelROB } from 'src/types/ViewModel/Bunkering/FuelROB';
import { FuelRobColumn } from 'src/types/ViewModel/Bunkering/FuelRobColumn';
import { RobRowDataStyle, RobRowStyle } from './BunkerRob.styles';
import DebunkeringInput from './DebunkeringInput';
import { getMaxWeight } from '../BunkeringUtils';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { useAuthStore } from 'src/context/AuthContext';
import { CheckboxWrapper } from '../Bunkering.styles';
import { ListButton } from 'src/styles/button.style';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface BunkerRobRowProps {
    i: number;
    tank: Tank;
    rob: FuelROB;
    fuelRobsForTank: FuelROB[];
    previousColumn: FuelRobColumn;
    currentColumn: FuelRobColumn;
    hasConsumption: boolean;
    onChangeRob: (changeObject: InputChangeData, rob: FuelROB, column: FuelRobColumn) => void;
    onToggleSoundingsVerified: (changeObject: InputChangeData, rob: FuelROB, column: FuelRobColumn) => void;
    onChangeDebunkering: (changeObject: InputChangeData, debunkerValue: BunkerReceived, column: FuelRobColumn, oldValue: number) => void;
    openFuelTransferModal: (fromRob: FuelROB) => void;
}

const BunkerRobRow: React.FC<BunkerRobRowProps> = ({
    i,
    tank,
    rob,
    fuelRobsForTank,
    previousColumn,
    currentColumn,
    hasConsumption,
    onChangeRob,
    onToggleSoundingsVerified,
    onChangeDebunkering,
    openFuelTransferModal,
}) => {
    const { bunkerings, bunkerLossData } = useBunkerContext();
    const { role } = useAuthStore();
    const [robMatchesCalculated, setRobMatchesCalculated] = useState<boolean>(true);
    const [previousRob, setPreviousRob] = useState<FuelROB | undefined>(undefined);
    const [otherRobsInSameTank, setOtherRobsInSameTank] = useState<FuelROB[]>([]);

    useEffect(() => {
        setPreviousRob(previousColumn?.fuelRobs.find((_rob) => _rob.tankId === tank.tankId && rob.bunkeringGuid === _rob?.bunkeringGuid));
    }, [previousColumn, tank.tankId, rob.bunkeringGuid]);

    useEffect(() => {
        setOtherRobsInSameTank(fuelRobsForTank.filter((_rob) => _rob.tankId === tank.tankId && rob.bunkeringGuid !== _rob?.bunkeringGuid));
    }, [fuelRobsForTank, tank.tankId, rob.bunkeringGuid]);

    useEffect(() => {
        setRobMatchesCalculated(rob.rob === rob.calculatedRob);
    }, [rob]);

    const handleChangeRob = (changeObject: InputChangeData, rob: FuelROB, calculatedRobRounded: number) => {
        setRobMatchesCalculated(+changeObject.event.target.value === calculatedRobRounded);
        onChangeRob(changeObject, rob, currentColumn);
    };

    const getBunkerLossDataForTank = () => {
        return bunkerLossData
            .filter((l) => l.noonReportId === currentColumn.noonReportId && l.tankId === tank.tankId && l.bunkeringGuid === rob.bunkeringGuid)
            .reduce((acc, l) => acc + l.quantityMT, 0);
    };

    return (
        <div key={`${tank.tankId}_${rob.bunkeringGuid}_${rob?.noonReportId}_rob_${i}`}>
            {rob.displayOnly ? (
                <RobRowStyle key={`${tank.tankId}_${rob.bunkeringGuid}_rob_s`} hasConsumption={hasConsumption} indent>
                    <RobRowDataStyle />
                    <RobRowDataStyle flex={1.25} />
                    <RobRowDataStyle flex={2}>
                        {!currentColumn.debunkeringEventId && (
                            <OdfjellInputComponent
                                key={`${tank.tankId}_${rob.bunkeringGuid}_rob_input`}
                                type="number"
                                name="rob"
                                value={previousRob?.rob ?? 0}
                                isReadOnly
                                unit="MT"
                            ></OdfjellInputComponent>
                        )}
                    </RobRowDataStyle>
                    <RobRowDataStyle flex={1.6} />
                </RobRowStyle>
            ) : (
                <RobRowStyle
                    key={`${tank.tankId}_${rob.bunkeringGuid}_rob_s`}
                    indent={!currentColumn.debunkeringEventId}
                    hasConsumption={hasConsumption}
                >
                    <RobRowDataStyle flex={currentColumn.noonReportId ? 1 : 0.7} hasChange={rob.consumed > 0}>
                        {currentColumn.debunkeringEventId ? (
                            <DebunkeringInput fuelRobColumn={currentColumn} tankId={tank.tankId} onChangeDebunkering={onChangeDebunkering} />
                        ) : currentColumn.noonReportId ? (
                            rob.consumed * -1
                        ) : (
                            Number((rob.calculatedRob - (previousRob?.rob ?? 0)).toFixed(2))
                        )}
                    </RobRowDataStyle>
                    {bunkerLossData.some((l) => l.noonReportId === currentColumn.noonReportId) && (
                        <RobRowDataStyle flex={currentColumn.noonReportId ? 1 : 0.8} hasChange={getBunkerLossDataForTank() > 0}>
                            {getBunkerLossDataForTank() * -1}
                        </RobRowDataStyle>
                    )}
                    <RobRowDataStyle flex={currentColumn.noonReportId ? 1 : 0.5} debunkeringCalculatedRob={!!currentColumn.debunkeringEventId}>
                        {rob.calculatedRob}
                    </RobRowDataStyle>
                    {!currentColumn.debunkeringEventId && (
                        <RobRowDataStyle flex={currentColumn.noonReportId ? 2 : 1.2}>
                            <OdfjellInputComponent
                                key={`${tank.tankId}_${rob.bunkeringGuid}_rob_input`}
                                type="number"
                                name="rob"
                                value={rob.rob}
                                min={0}
                                max={getMaxWeight(tank, bunkerings.find((b) => b.bunkeringGuid === rob.bunkeringGuid)?.bdnLiterWeight ?? 1)}
                                onChange={(changeObject) => handleChangeRob(changeObject, rob, rob.calculatedRob)}
                                isReadOnly={rob.noonReportId === 0 || !role.isWriter || !!currentColumn.debunkeringEventId}
                                unit="MT"
                                validationInTooltip
                                warningText={!robMatchesCalculated ? `Warning: ROB deviating from calculated: ${rob.calculatedRob} MT` : ''}
                            />
                        </RobRowDataStyle>
                    )}
                    {!currentColumn.debunkeringEventId && (
                        <RobRowDataStyle flex={0.9}>
                            <CheckboxWrapper>
                                <OdfjellInputComponent
                                    key={`${tank.tankId}_${rob.bunkeringGuid}_rob_input_verified`}
                                    type="checkbox"
                                    name="soundingVerified"
                                    value={rob.soundingVerified}
                                    onChange={(changeObject) => onToggleSoundingsVerified(changeObject, rob, currentColumn)}
                                    isReadOnly={rob.noonReportId === 0 || !role.isWriter}
                                />
                            </CheckboxWrapper>
                        </RobRowDataStyle>
                    )}
                    {currentColumn.noonReportId && (
                        <RobRowDataStyle>
                            <ListButton onClick={() => openFuelTransferModal(rob)} disabled={!role.isWriter}>
                                <FontAwesomeIcon title="Transfer to other tank" icon="exchange-alt" />
                            </ListButton>
                        </RobRowDataStyle>
                    )}
                </RobRowStyle>
            )}
            {otherRobsInSameTank && otherRobsInSameTank.length > 0 && i >= otherRobsInSameTank.length && (
                <RobRowStyle key={`${tank.tankId}_${rob.bunkeringGuid}_rob_s2`} indent>
                    {bunkerLossData.some((l) => l.noonReportId === currentColumn.noonReportId) && (
                        <RobRowDataStyle flex={currentColumn.noonReportId ? 1 : 0.5} />
                    )}
                    <RobRowDataStyle flex={currentColumn.noonReportId ? 1 : 0.5} />
                    <RobRowDataStyle flex={currentColumn.noonReportId ? 1 : 0.5} />
                    {!currentColumn.debunkeringEventId && (
                        <RobRowDataStyle flex={currentColumn.noonReportId ? 1.6 : 1.2} sumRow>{`${Number(
                            fuelRobsForTank.reduce((acc, _rob) => acc + _rob.rob, 0).toFixed(2)
                        )} MT`}</RobRowDataStyle>
                    )}
                    <RobRowDataStyle />
                    {currentColumn.noonReportId && <RobRowDataStyle />}
                </RobRowStyle>
            )}
        </div>
    );
};

export default BunkerRobRow;
